import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Clock, CheckCircle2, AlertTriangle, RefreshCw, Camera } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  synced: { label: "Sincronizzata", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  failed: { label: "Non riuscita", cls: "bg-rose-100 text-rose-700 border-rose-300", icon: AlertTriangle },
};

export default function ReportSalvataggiTemporaneiPage() {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pending-opportunities/report`, { withCredentials: true });
      setData(data || { items: [], counts: {}, total: 0 });
    } catch {
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setRetrying((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/pending-opportunities/${id}/retry`, {}, { withCredentials: true });
      toast[data?.synced ? "success" : "info"](data?.synced ? "Sincronizzata su GHL ✓" : "GHL ancora occupato — riproveremo a breve");
    } catch {
      toast.error("Errore durante il tentativo");
    } finally {
      setRetrying((r) => ({ ...r, [id]: false }));
      load();
    }
  };

  const c = data.counts || {};

  return (
    <div className="max-w-5xl mx-auto px-4 pt-20 pb-6" data-testid="report-salvataggi-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-slate-800">Salvataggi temporanei</h1>
        <button
          type="button"
          onClick={load}
          data-testid="report-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm px-3 py-1.5 transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Aggiorna
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Opportunità create quando GHL era occupato (rate-limit). Qui vedi dove sono finite: in attesa, sincronizzate su GHL o non ancora riuscite.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="count-pending">
          <div className="text-[11px] uppercase tracking-wide text-amber-700">In attesa</div>
          <div className="text-2xl font-bold text-amber-800 tabular-nums">{c.pending || 0}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="count-synced">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700">Sincronizzate</div>
          <div className="text-2xl font-bold text-emerald-700 tabular-nums">{c.synced || 0}</div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="count-failed">
          <div className="text-[11px] uppercase tracking-wide text-rose-700">Non riuscite</div>
          <div className="text-2xl font-bold text-rose-700 tabular-nums">{c.failed || 0}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400" data-testid="report-loading">Caricamento…</div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-2xl" data-testid="report-empty">
          Nessun salvataggio temporaneo presente.
        </div>
      ) : (
        <div className="space-y-2" data-testid="report-list">
          {data.items.map((it) => {
            const s = STATUS[it.status] || STATUS.pending;
            const Icon = s.icon;
            return (
              <div
                key={it.id}
                data-testid={`report-item-${it.id}`}
                className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>
                      <Icon className="w-3 h-3" /> {s.label}
                    </span>
                    <span className="text-sm font-semibold text-slate-800 truncate">{it.contact_name || "—"}</span>
                    {it.assicurazione_name ? <span className="text-xs text-slate-400">· {it.assicurazione_name}</span> : null}
                    {it.has_photos ? <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><Camera className="w-3 h-3" /> foto da riaggiungere</span> : null}
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1 tabular-nums">
                    €{Math.round(it.total_cost || 0)} · {it.piano?.gruppi || 0} gruppi / {it.piano?.trattamenti || 0} prestazioni · creato il {fmtDateTime(it.created_at)}
                    {it.created_by_email ? ` · ${it.created_by_email}` : ""}
                  </div>
                  {it.status === "synced" && it.synced_opp_id ? (
                    <div className="text-[12px] text-emerald-600 mt-0.5">Sincronizzata il {fmtDateTime(it.synced_at)} — opp GHL: {it.synced_opp_id}</div>
                  ) : null}
                  {it.status !== "synced" && it.attempts ? (
                    <div className="text-[12px] text-rose-500 mt-0.5">Tentativi: {it.attempts} · ultimo errore: {it.last_error || "—"}</div>
                  ) : null}
                </div>
                {it.status !== "synced" ? (
                  <button
                    type="button"
                    onClick={() => retry(it.id)}
                    disabled={retrying[it.id]}
                    data-testid={`report-retry-${it.id}`}
                    className="inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 transition-colors disabled:opacity-60"
                  >
                    {retrying[it.id] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Riprova ora
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
